import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { LogType } from '../types/visualizer';
import { TabType } from '../components/control-bar';

export interface NavigationTarget {
  type: LogType;
  index: number;
}

interface LogNavigationContextType {
  navigateToLog: (target: NavigationTarget) => void;
  currentLogTarget: NavigationTarget | null;
  setActiveTab: (tabType: TabType) => void;
  activeTab: TabType | null;
  navigationTrigger: number;
  navigateToNode: (target: NavigationTarget) => void;
  currentNodeTarget: NavigationTarget | null;
  clearNodeTarget: () => void;
  clearLogTarget: () => void;
}

export const LogNavigationContext = createContext<LogNavigationContextType | undefined>({
  navigateToLog: () => {},
  currentLogTarget: null,
  setActiveTab: () => {},
  activeTab: null,
  navigationTrigger: 0,
  navigateToNode: () => {},
  currentNodeTarget: null,
  clearNodeTarget: () => {},
  clearLogTarget: () => {}
});

export const LogNavigationProvider = ({ children }: { children: ReactNode }) => {
  const [currentLogTarget, setCurrentLogTarget] = useState<NavigationTarget | null>(null);
  const [activeTab, setActiveTabState] = useState<TabType | null>(null);
  const [navigationTrigger, setNavigationTrigger] = useState(0);
  const [currentNodeTarget, setCurrentNodeTarget] = useState<NavigationTarget | null>(null);

  const navigateToLog = (target: NavigationTarget) => {
    const tabMapping = {
      [LogType.INTERACTION_LOG]: TabType.INTERACTION_LOGS,
      [LogType.FLOW_LOG]: TabType.FLOW_LOGS,
      [LogType.TOOL_CITATIONS_LOG]: TabType.TOOL_CITATIONS
    };

    const targetTab = tabMapping[target.type];
    if (targetTab) {
      setActiveTabState(targetTab);
      setNavigationTrigger((prev) => prev + 1);
    }

    setCurrentLogTarget(target);
  };

  const navigateToNode = useCallback((target: NavigationTarget) => {
    setCurrentNodeTarget(target);
  }, []);

  const clearNodeTarget = useCallback(() => {
    setCurrentNodeTarget(null);
  }, []);

  const setActiveTab = useCallback((tabType: TabType) => {
    setActiveTabState(tabType);
  }, []);

  const clearLogTarget = useCallback(() => {
    setCurrentLogTarget(null);
  }, []);

  return (
    <LogNavigationContext.Provider
      value={{
        navigateToLog,
        currentLogTarget,
        setActiveTab,
        activeTab,
        navigationTrigger,
        navigateToNode,
        currentNodeTarget,
        clearNodeTarget,
        clearLogTarget
      }}
    >
      {children}
    </LogNavigationContext.Provider>
  );
};

export function useLogNavigation() {
  const context = useContext(LogNavigationContext);
  if (context === undefined) {
    throw new Error('useLogNavigation must be used within a LogNavigationProvider');
  }

  return context;
}
